import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import styles from "./finecoFullBarChart.module.scss";
import colorsStore from "shared/store/colorsStore";
import { colorCharts } from "data/palettes";
import { hexToRgba } from "utils/misc";

const renderCustomBarLabel = (props) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const { x, y, width, height, value, index } = props;

  const textContent = `${value.toFixed(2)}%`;

  // Fixed padding on each side of the text
  const fixedPadding = 4;

  // Estimate text width based on content
  const digitCount = textContent.replace(/[^0-9]/g, "").length;
  const hasDecimal = textContent.includes(".");
  const hasPercentage = textContent.includes("%");

  const estimatedTextWidth =
    digitCount * 8 + (hasDecimal ? 3 : 0) + (hasPercentage ? 10 : 0);

  // Calculate rectangle width with consistent padding
  const rectWidth = estimatedTextWidth + fixedPadding * 2;

  // const backgroundColor =
  //   index % 2 === 0
  //     ? colorsConfig.productCardBarChartLabel2
  //     : colorsConfig.productCardBarChartLabel1;

  const backgroundColorHex =
    colorsConfig[colorCharts[index % colorCharts.length]];
  const backgroundColor = hexToRgba(backgroundColorHex, 0.2);

  return (
    <g>
      <rect
        x={x + width + 7}
        y={y + height / 2 - 12}
        width={rectWidth}
        height={23}
        fill={backgroundColor}
        rx={5}
      />
      <text
        x={x + width + 9 + fixedPadding}
        y={y + height / 2 + 0}
        fill={colorsConfig.FinecoBlack}
        dominantBaseline="middle"
        textAnchor="start"
        fontSize={12}
        fontWeight={700}
        fontFamily="Proxima Nova"
      >
        {textContent}
      </text>
    </g>
  );
};

const CustomYAxisTick = (props) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const { x, y, payload } = props;

  const truncateText = (text, maxLength = 28) => {
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={-10}
        y={0}
        dy={4}
        textAnchor="end"
        fill={colorsConfig.FinecoBlack}
        fontSize={14}
        fontFamily="Proxima Nova"
        fontWeight={400}
        className="yaxis-label"
      >
        {truncateText(payload.value)}
      </text>
    </g>
  );
};

const CustomXAxisTick = (props) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const { x, y, payload } = props;

  let text = "";
  if (payload.value === 0) {
    text = "0";
  } else {
    text = payload.value.toFixed(2) + "%";
  }

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16} // Increase this value to move labels down
        textAnchor="middle" // Center the text at the tick position
        fill={colorsConfig.FinecoBlack}
        fontSize={12}
        fontFamily="Proxima Nova"
        fontWeight={400}
      >
        {text}
      </text>
    </g>
  );
};

const calculateNiceTicks = (maxValue) => {
  // Round up to the next 5
  const roundedMax = Math.ceil(maxValue / 5) * 5;

  // Calculate a nice step size (divide by 4 to get 5 ticks including 0)
  const step = roundedMax / 4;

  // Generate the ticks
  return Array.from({ length: 5 }, (_, i) => i * step);
};

const FinecoFullBarChartComponent = ({ data, title, section_label = null, section_line_after = null }) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const maxWeight = Math.max(...data.map((item) => item.weight));
  const ticks = calculateNiceTicks(maxWeight);

  const formattedData = data.map((item, index) => ({
    ...item,
    id: `item-${index}`,
    weight: parseFloat(item.weight.toFixed(2)),
    fill: colorsConfig[colorCharts[index % colorCharts.length]],
  }));

  const chartHeight = formattedData.length * 40 + 54;

  // Find section line position for line placement - only if section_line_after is provided
  const sectionIndex = section_line_after
    ? formattedData.findIndex(item => item.label === section_line_after)
    : -1;
  const lineTopPosition = sectionIndex >= 0
    ? 15 + (sectionIndex + 1) * 40 - 7
    : 0;

  return (
    <div>
      <div className={styles.subSectionTitle}>{title}</div>
      <div className={styles.chartContainer} style={{ position: 'relative' }}>
        <ResponsiveContainer width="100%" height={chartHeight} debounce={500}>
          <BarChart
            data={formattedData}
            layout="vertical"
            margin={{ top: 15, right: 80, left: 120, bottom: 30 }}
            barGap={30}
          >
            <CartesianGrid horizontal={false} vertical={false} />
            <XAxis
              type="number"
              domain={[0, ticks[ticks.length - 1]]}
              ticks={ticks}
              tick={<CustomXAxisTick />}
              tickLine={false}
              axisLine={{ stroke: colorsConfig.Gray3 }}
            />
            <YAxis
              type="category"
              dataKey="label"
              width={100}
              interval={0}
              tick={<CustomYAxisTick />}
              axisLine={{ stroke: colorsConfig.Gray3 }}
              tickLine={false}
            />
            <Bar
              dataKey="weight"
              radius={[0, 4, 4, 0]}
              barSize={26}
              minPointSize={10}
              isAnimationActive={false}
            >
              <LabelList
                dataKey="weight"
                position="right"
                content={renderCustomBarLabel}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        {/* Conditional section label */}
        {section_label && (
          <div
            style={{
              position: 'absolute',
              top: '2px',
              left: '120px',
              fontSize: '12px',
              fontWeight: '600',
              color: colorsConfig.FinecoBlack || '#000',
              fontFamily: 'Proxima Nova',
              pointerEvents: 'none',
            }}
          >
            {section_label}
          </div>
        )}

        {/* Conditional CSS-positioned line */}
        {section_line_after && sectionIndex >= 0 && (
          <div
            style={{
              position: 'absolute',
              top: `${lineTopPosition}px`,
              left: '120px',
              right: '80px',
              height: '2px',
              background: `repeating-linear-gradient(to right, ${colorsConfig.Gray3 || '#ccc'} 0px, ${colorsConfig.Gray3 || '#ccc'} 5px, transparent 5px, transparent 10px)`,
              pointerEvents: 'none',
            }}
          />
        )}
      </div>
    </div>
  );
};

const FinecoFullBarChart = ({ data, title, section_label = null, section_line_after = null }) => {
  return (
    <div className={styles.container}>
      <FinecoFullBarChartComponent
        data={data}
        title={title}
        section_label={section_label}
        section_line_after={section_line_after}
      />
    </div>
  );
};

export default FinecoFullBarChart;
